import {
  Box,
  Button,
  Card,
  ChakraProvider,
  Container,
  Grid,
  HStack,
  Input,
  InputGroup,
  InputRightElement,
  SimpleGrid,
  Spacer,
  Stack,
  Text,
  useMediaQuery,
} from "@chakra-ui/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ArrowLeft, ArrowRight, Menu, Search } from "react-feather";
import { useLocation, useNavigate } from "react-router-dom";
import { fetchTutorials } from "../../api/tutorials";
import TutorialCard from "./tutorialCard";
import { TutorialPlayerPage } from "./tutorialVideoPlayer";
import ClientDashboardLayout from "../dashboard/dashboardLayout";
import theme from "../../theme";

export const TutorialVault = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [tutorials, setTutorials] = useState([]);
  const [search, setSearch] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [tutorialData, setTutorialData] = useState(null);
  const [toggleView, setToggleView] = useState(false);

  const [prevPage, setPrevPage] = useState(null);
  const [nextPage, setNextPage] = useState(null);
  const [pageRange, setPageRange] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);

  const [isMobile] = useMediaQuery("(max-width: 1280px)");

  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );
  const TutorialId = useMemo(
    () => searchParams.get("id") || null,
    [searchParams]
  );
  const [loadedId, setLoadedId] = useState(null);

  const navigateRef = useRef(navigate);
  useEffect(() => {
    navigateRef.current = navigate;
  }, [navigate]);

  const searchRef = useRef(location.search);
  useEffect(() => {
    searchRef.current = location.search;
  }, [location.search]);

  useEffect(() => {
    const id = TutorialId;

    if (id && id !== loadedId) {
      fetchTutorials({ id }).then((res) => {
        setTutorialData(res?.results?.[0] || null);
        setToggleView(true);
        setLoadedId(id);
      });
    } else if (!id && loadedId) {
      setTutorialData(null);
      setToggleView(false);
      setLoadedId(null);
    }
  }, [TutorialId, loadedId]);

  useEffect(() => {
    if (!tutorialData?.id) return;

    const currentParams = new URLSearchParams(searchRef.current);
    const currentId = currentParams.get("id");
    const newId = String(tutorialData.id);

    if (currentId !== newId) {
      currentParams.set("id", newId);
      navigateRef.current({
        search: currentParams.toString(),
      });
    }

    setToggleView(true);
  }, [tutorialData]);

  const refreshTutorials = useCallback(
    async (page = 1) => {
      try {
        setCurrentPage(page);

        const params = { searchTerm: search, page };
        const res = await fetchTutorials(params);

        setTutorials(res);
        setPageRange(res.page_range || []);

        const nextUrlObj = res.next ? new URL(res.next) : null;
        setNextPage(
          nextUrlObj ? parseInt(nextUrlObj.searchParams.get("page"), 10) : null
        );

        const prevUrlObj = res.previous ? new URL(res.previous) : null;
        setPrevPage(
          prevUrlObj
            ? parseInt(prevUrlObj.searchParams.get("page"), 10) || 1
            : null
        );
      } catch (err) {
        console.error(err);
      }
    },
    [search] // only re-run when search term changes
  );

  useEffect(() => {
    setPageRange([]);
    setNextPage(null);
    setPrevPage(null);
    setCurrentPage(1);
    refreshTutorials(1);
  }, [search, refreshTutorials]);

  return (
    <ClientDashboardLayout>
      <ChakraProvider theme={theme}>
        <Container width="100%" maxW="100%" bg="#F4F4F2" p="0" minH={"100vh"}>
          {isMobile && (
            <Card
              position="fixed"
              zIndex={600}
              borderRadius={0}
              h="72px"
              borderBottom="1px solid #e0e0e0"
              width="100%"
            >
              <HStack
                justifyContent="space-between"
                alignItems="center"
                h="100%"
                w="100%"
                px={4}
              >
                <Button
                  w="50px"
                  h="50px"
                  p={2}
                  bg="white"
                  _hover={{ color: "white" }}
                  _active={{ color: "white" }}
                  onClick={() => {
                    window.dispatchEvent(new Event("sidebar:expand"));
                  }}
                >
                  <Menu
                    style={{ width: "50px", height: "100%" }}
                    color="black"
                  />
                </Button>
                <Spacer />
                <InputGroup width="230px" height="36px" mr="7px">
                  <Input
                    height="36px"
                    fontSize="14px"
                    placeholder="Search Tutorials..."
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                  />
                  <InputRightElement width="44px" height="36px">
                    <Button
                      width="44px"
                      height="36px"
                      p={0}
                      minW="unset"
                      borderLeftRadius={0}
                      onClick={() => {
                        setSearch(inputValue);
                        setToggleView(false);
                        navigate(location.pathname);
                      }}
                    >
                      <Search size="20px" />
                    </Button>
                  </InputRightElement>
                </InputGroup>
              </HStack>
            </Card>
          )}

          {!isMobile && (
            <Card
              position="fixed"
              mt={isMobile ? "72px" : "0px"}
              borderRadius={0}
              width={isMobile ? "100%" : "auto"}
              height="75px"
              display="flex"
              alignItems="center"
              zIndex={600}
              borderBottom="1px solid #e0e0e0"
              mb={1}
            >
              <Grid
                templateColumns="1fr 200fr 1fr"
                width="100%"
                maxW={"100%"}
                alignItems="center"
                padding="0px 20px"
                height="100%"
              >
                <Box
                  gridRow={1}
                  gridColumn={1}
                  textAlign={"left"}
                  fontWeight={700}
                  fontSize={"26px"}
                  whiteSpace={"nowrap"}
                >
                  Lucro Tutorials
                </Box>
                <Box
                  gridRow={1}
                  gridColumn={3}
                  display="flex"
                  justifyContent="flex-end"
                >
                  <HStack>
                    <>
                      <InputGroup width="320px" height="36px" mr={"7px"}>
                        <Input
                          height="36px"
                          placeholder="Search Tutorials..."
                          value={inputValue}
                          onChange={(e) => setInputValue(e.target.value)}
                        />
                        <InputRightElement width="44px" height="36px">
                          <Button
                            width="44px"
                            height="36px"
                            p={0}
                            minW="unset"
                            borderLeftRadius={0}
                            onClick={() => {
                              setSearch(inputValue);
                              setToggleView(false);
                              navigate(location.pathname);
                            }}
                          >
                            <Search size="24px" />
                          </Button>
                        </InputRightElement>
                      </InputGroup>
                    </>
                  </HStack>
                </Box>
              </Grid>
            </Card>
          )}

          {toggleView ? (
            <TutorialPlayerPage
              tutorialData={tutorialData}
              setVault={setToggleView}
            />
          ) : (
            <Stack p="24px" minH="100vh">
              {tutorials?.results?.length > 0 && (
                <Stack mt="74px">
                  <SimpleGrid
                    columns={{ base: 1, sm: 2, lg: 3 }}
                    spacing="12px 20px"
                  >
                    {tutorials.results.map((tutorial, idx) => (
                      <TutorialCard
                        key={idx}
                        tutorialData={tutorial}
                        setSelectedTutorial={setTutorialData}
                        toggleView={setToggleView}
                      />
                    ))}
                  </SimpleGrid>
                </Stack>
              )}

              <HStack
                pt="10px"
                spacing={!isMobile ? 3 : 0}
                justify="center"
                align="center"
              >
                <Button
                  variant="unstyled"
                  isDisabled={!prevPage}
                  onClick={() => refreshTutorials(prevPage)}
                  _disabled={{
                    cursor: "default",
                    opacity: 0.6,
                    pointerEvents: "none",
                  }}
                >
                  <HStack>
                    {!isMobile && <ArrowLeft />}
                    <Text>Prev</Text>
                  </HStack>
                </Button>

                {pageRange.map((item, idx) =>
                  typeof item === "number" ? (
                    <Button
                      key={idx}
                      variant="unstyled"
                      onClick={() => refreshTutorials(item)}
                      textColor={item === currentPage ? "white" : "black"}
                      backgroundColor={
                        item === currentPage ? "#536DEC" : "white"
                      }
                      _hover={{ color: "#627CFB" }}
                    >
                      {item}
                    </Button>
                  ) : (
                    <Text key={idx} color="black">
                      ...
                    </Text>
                  )
                )}

                <Button
                  variant="unstyled"
                  isDisabled={!nextPage}
                  onClick={() => refreshTutorials(nextPage)}
                  _disabled={{
                    cursor: "default",
                    opacity: 0.6,
                    pointerEvents: "none",
                  }}
                >
                  <HStack>
                    <Text>Next</Text>
                    {!isMobile && <ArrowRight />}
                  </HStack>
                </Button>
              </HStack>
            </Stack>
          )}
        </Container>
      </ChakraProvider>
    </ClientDashboardLayout>
  );
};
